import React from 'react';
import ALink from 'tradera/components/alink/alink';
import { GtmService } from 'tradera/services/google-tagmanager-service';
import { ITEM_CARD_IMAGE_MODES } from './item-card-parts/item-card-image-modes';
import { AnimatedPriceLabel } from './item-card-parts/animated-price-label';
import { EndTimeLabel } from './item-card-parts/end-time-label';
import { AttributeButtonsList } from './item-card-parts/attribute-buttons-list';
import { BidsLabel } from './item-card-parts/bids-label';
import { DiscountBadge } from './item-card-parts/discount-badge';
import { DiscountedPriceLabel } from './item-card-parts/disacount-price-label';
import { FixedPriceLabel } from './item-card-parts/fixed-price-label';
import { ItemCardImage } from './item-card-parts/item-card-image';
import { NewTodayBadge } from './item-card-parts/new-today-badge';
import { OnDisplayBadge } from './item-card-parts/on-display-badge';
import { PriceLabel } from './item-card-parts/price-label';
import { QuickViewButton } from './item-card-parts/quick-view-button';
import { SettingsButton } from './item-card-parts/settings-button';
import { WishListButton } from './item-card-parts/wish-list-button';
import { selectIsSwedishVisitor } from 'tradera/state/shipping-region/selectors';
import { useDayJs } from 'tradera/hooks/use-day-js';
import { useIsPostHydrationClientRender } from 'tradera/apps/nextweb/hooks/use-is-post-hydration-client-render';
import { useTranslator, useUrlLocalizer } from 'tradera/lang/translate';
import classnames from 'classnames';
import { useAppSelector } from 'tradera/state/hooks';
import type { ItemDto } from 'tradera/state/services/types/touchweb-search-api.types';
import {
    AuctionType,
    ShippingOptionProvider
} from 'tradera/state/services/types/touchweb-search-api.types';
import type { ItemDtoPlaceHolder } from './generate-item-card-placeholders';
import type {
    ItemCardDto,
    ItemCardViewModel
} from 'tradera/state/services/types/webapi-discover-generated';
import type { ITEM_LAYOUT } from 'tradera/constants/ui';
import { Space } from '../atoms/space/space';
import { getVisibleAttributes } from './item-card-parts/attribute-list-helper';

export type PossibleItemTypes =
    | ItemDto
    | ItemCardDto
    | ItemDtoPlaceHolder
    | ItemCardViewModel;

const hasItemId = (item: unknown): item is { itemId: number } =>
    item !== null && typeof item === 'object' && 'itemId' in item;

export type ItemCardProps = {
    animatePriceLabel?: boolean;
    className?: string;
    gaCategory?: string;
    imageMode?: ITEM_CARD_IMAGE_MODES;
    item?: PossibleItemTypes;
    itemLayout?: ITEM_LAYOUT;
    isSiteWidthFull?: boolean;
    onAddToWishlist?: (itemId: ItemDto['itemId']) => void;
    onRemoveFromWishlist?: (itemId: ItemDto['itemId']) => void;
    onQuickView?: (item: PossibleItemTypes) => void;
    onSettings?: (item: PossibleItemTypes) => void;
    onAttributeButtonClick?: () => void;
    showBidStatus?: boolean;
    showTitleOnOneLine?: boolean;
    showShippingOrOnDisplay?: boolean;
    isShopOwnedByCurrentMember?: boolean;
    showSellerAlias?: boolean;
    showFallback?: boolean;
};

export const ItemCard = ({
    animatePriceLabel = false,
    className,
    gaCategory,
    imageMode = ITEM_CARD_IMAGE_MODES.SQUARE,
    isSiteWidthFull,
    item,
    itemLayout,
    onAddToWishlist,
    onQuickView,
    onRemoveFromWishlist,
    onAttributeButtonClick,
    onSettings,
    showBidStatus,
    showTitleOnOneLine = false,
    showShippingOrOnDisplay = true,
    showSellerAlias,
    showFallback,
    isShopOwnedByCurrentMember = false,
    ...props
}: ItemCardProps) => {
    const isPostHydrationClientRender = useIsPostHydrationClientRender();
    const ready = !!(item as ItemDto)?.itemUrl && isPostHydrationClientRender;
    const { t } = useTranslator();

    const withListItemIds = useAppSelector(
        (state) => state.items.wishListItemIds
    ) as number[];
    const existsInWishList =
        hasItemId(item) && withListItemIds.includes(item.itemId);

    const isSwedishVisitor = useAppSelector(selectIsSwedishVisitor);
    const { toLocalizedUrl } = useUrlLocalizer();
    const dayjs = useDayJs();

    const auctionType = (item as ItemDto).itemType;

    const isAuctionBin = auctionType === AuctionType.AuctionBin;

    const totalBids = (item as ItemDto)?.totalBids;

    const shouldShowBinPrice =
        isAuctionBin && (item as ItemDto).isActive && totalBids === 0;

    const shouldShowBuyNowPriceLabel =
        [AuctionType.PureBin, AuctionType.ShopItem].includes(auctionType) ||
        (isAuctionBin && (item as ItemDto).isActive && totalBids === 0);

    const isShopItem = auctionType === AuctionType.ShopItem;

    const hasDiscount = ((item as ItemDto)?.discountRate ?? 0) > 0;

    const shouldShowBids =
        auctionType === AuctionType.Auction || (isAuctionBin && totalBids > 0);

    const isPickupOnly =
        (item as ItemDto).shippingOptions?.length === 1 &&
        [
            ShippingOptionProvider.Takeaway,
            ShippingOptionProvider.TakeawayOnly
        ].includes((item as ItemDto).shippingOptions[0].type);

    const startDate = (item as ItemDto).startDate || '';
    const isNewToday =
        !!startDate && dayjs(startDate).isAfter(dayjs().subtract(1, 'day'));
    const itemUrl = (item as ItemDto)?.itemUrl
        ? toLocalizedUrl((item as ItemDto).itemUrl)
        : null;

    const visibleAttributes = getVisibleAttributes(
        (item as ItemDto)?.attributes
    );

    const hasAttributes = (visibleAttributes?.length ?? 0) > 0;

    const textClassNames =
        (hasAttributes && onAttributeButtonClick) || showTitleOnOneLine
            ? 'text-truncate-one-line'
            : 'text-truncate-two-lines';

    const handleSettings = (event: React.MouseEvent) => {
        event.preventDefault();
        if (!item) return;
        onSettings?.(item);
    };

    const handleQuickview = (event: React.MouseEvent) => {
        event.preventDefault();
        if (!item) return;
        if (!onQuickView) return;
        onQuickView(item);
    };

    const handleWishlist = (event: React.MouseEvent) => {
        if (!onRemoveFromWishlist) return;
        if (!onAddToWishlist) return;
        if (!item) return;
        event.preventDefault();

        if (existsInWishList) {
            onRemoveFromWishlist(item.itemId);
        } else {
            onAddToWishlist(item.itemId);
        }
    };

    const onNavigate = () => {
        if (gaCategory && item) {
            GtmService.trackAction(
                gaCategory,
                'Navigation',
                item.itemId?.toString() ?? ''
            );
        }
    };

    // The particular order of the badges has been decided by UX
    const getSingleBadge = () => {
        if (showSellerAlias && (item as ItemDto)?.sellerAlias) {
            return (
                <span className="seller-alias-badge badge badge-dark text-nowrap d-inline-block text-truncate">
                    {(item as ItemDto).sellerAlias}
                </span>
            );
        }

        if (showBidStatus && (item as ItemDto).isLeading) {
            return (
                <span className="mt-2 badge badge-success text-nowrap">
                    {t('itemCard_leading')}
                </span>
            );
        }

        if (showBidStatus && (item as ItemDto).isOverbidden) {
            return (
                <span className="mt-2 badge badge-danger text-nowrap">
                    {t('itemCard_overbidden')}
                </span>
            );
        }

        if (showShippingOrOnDisplay && (item as ItemDto).onDisplay) {
            return <OnDisplayBadge className="mt-2 mr-2" />;
        }

        if (isNewToday) {
            return <NewTodayBadge className="mr-1" />;
        }

        if (hasDiscount) {
            return (
                <DiscountBadge
                    className="mr-1"
                    discountRate={(item as ItemDto).discountRate ?? 0}
                />
            );
        }

        if (
            showShippingOrOnDisplay &&
            isSwedishVisitor &&
            (item as ItemDto).hasFreeShipping
        ) {
            return (
                <span className="mt-2 mr-2 badge badge-branded text-nowrap">
                    {t('itemCard_freeShipping')}
                </span>
            );
        }

        if (isPickupOnly) {
            return (
                <span className="mt-2 mr-2 badge badge-white text-nowrap">
                    {t('itemCard_pickup')}
                </span>
            );
        }
    };

    return (
        <div
            id={`item-card-${item?.itemId}`}
            data-item-loaded={ready ? 'true' : 'false'}
            className={classnames(['item-card-new', className], {
                'item-card-new-highlight': (item as ItemDto).displayHighlighted
            })}
            {...props}>
            <div className="item-card-inner-wrapper">
                <ItemCardImage
                    className={classnames('mb-2', {
                        'cursor-pointer': !!itemUrl
                    })}
                    onClick={onNavigate}
                    imageHeight={(item as ItemDto)?.imageHeight}
                    imageMode={imageMode}
                    imageUrl={
                        (item as ItemDto)?.imageUrl ||
                        (item as ItemDto).imageNormalSizeUrl
                    }
                    imageWidth={(item as ItemDto)?.imageWidth}
                    imageSecondaryUrl={
                        (item as ItemDto)?.imageSecondaryUrl ||
                        (item as ItemDto).imageSecondaryGallerySizeUrl
                    }
                    itemUrl={itemUrl}
                    itemLayout={itemLayout}
                    isSiteWidthFull={isSiteWidthFull}
                    showFallback={showFallback}
                    shortDescription={(item as ItemDto)?.shortDescription}>
                    <div className="item-card-bottom-badges">
                        {getSingleBadge()}
                    </div>
                </ItemCardImage>
                <div className="item-card-buttons d-flex flex-column">
                    {ready && (
                        <>
                            {handleWishlist && (
                                <WishListButton
                                    className="mb-1"
                                    onClick={handleWishlist}
                                    showText={false}
                                    toggled={existsInWishList}
                                />
                            )}
                            {onQuickView && (
                                <QuickViewButton
                                    className="item-card-quickview-button btn btn-md-sm btn-translucent mb-1 d-none d-lg-inline-block"
                                    onClick={handleQuickview}
                                />
                            )}
                            {isShopOwnedByCurrentMember && (
                                <SettingsButton
                                    className="item-card-settings-button btn btn-md-sm btn-translucent mb-1"
                                    data-shop-item-settings-action
                                    onClick={handleSettings}
                                />
                            )}
                        </>
                    )}
                </div>
                <div className="item-card-title">
                    <span className="d-block">
                        {hasAttributes && onAttributeButtonClick ? (
                            <AttributeButtonsList
                                visibleAttributes={visibleAttributes}
                                onClick={onAttributeButtonClick}
                            />
                        ) : null}
                    </span>
                    {itemUrl ? (
                        <ALink
                            onClick={onNavigate}
                            className={textClassNames}
                            href={itemUrl}
                            id={`item_${item?.itemId}`}
                            role="button"
                            title={item?.shortDescription ?? ''}
                            useLink>
                            {item?.shortDescription}
                        </ALink>
                    ) : (
                        <p className={textClassNames}>
                            {item?.shortDescription}
                        </p>
                    )}
                </div>
                <div className="item-card-details">
                    {!isShopItem && (
                        <EndTimeLabel
                            className="ml-auto text-nowrap size-oslo text-gray-600"
                            endDate={(item as ItemDto)?.endDate}
                            isActive={(item as ItemDto)?.isActive}
                        />
                    )}
                    <Space wrap gap={[1, 0]} align="start" justify="between">
                        <Space wrap gap={[1, 0]}>
                            <span>
                                {animatePriceLabel ? (
                                    <AnimatedPriceLabel
                                        className="text-nowrap font-weight-bold font-hansen pr-1"
                                        data-testid="price"
                                        price={(item as ItemDto)?.price}
                                        hasDiscount={hasDiscount}
                                    />
                                ) : (
                                    <PriceLabel
                                        className="text-nowrap font-weight-bold font-hansen pr-1"
                                        data-testid="price"
                                        price={(item as ItemDto)?.price}
                                        hasDiscount={hasDiscount}
                                    />
                                )}
                                <span className="size-oslo">
                                    {hasDiscount && (
                                        <DiscountedPriceLabel
                                            className="px-1 text-nowrap"
                                            price={
                                                (item as ItemDto)
                                                    ?.priceBeforeDiscount ?? 0
                                            }
                                            discountRate={
                                                (item as ItemDto)
                                                    .discountRate ?? 0
                                            }
                                        />
                                    )}
                                    {shouldShowBuyNowPriceLabel && (
                                        <>
                                            {isAuctionBin ? (
                                                <span className="text-wrap">
                                                    {t('item-card_text_or')}{' '}
                                                    <FixedPriceLabel className="text-lowercase" />{' '}
                                                </span>
                                            ) : (
                                                <FixedPriceLabel className="text-nowrap pr-1" />
                                            )}
                                        </>
                                    )}
                                    {shouldShowBinPrice && (
                                        <PriceLabel
                                            data-testid="bin-price"
                                            className="text-nowrap text-inter-light pr-1"
                                            price={
                                                (item as ItemDto).buyNowPrice
                                            }
                                            hasDiscount={hasDiscount}
                                        />
                                    )}
                                    {shouldShowBids && (
                                        <BidsLabel
                                            className="text-nowrap"
                                            count={totalBids}
                                        />
                                    )}
                                </span>
                            </span>
                        </Space>
                    </Space>
                </div>
            </div>
        </div>
    );
};
